import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { BaseLayout } from '../../../layouts/base';
import { Heading, colors, fontWeight, Text, textLevel, Icons, Breadcrumbs } from '../../../packages';
import { getFaxById, toggleFaxStatus } from '../../../services/report-service/reportService.service';
import { sxStyles, useStyles } from './FaxDetail.styles';

export const FaxDetail = () => {
  const { jobId } = useParams();
  const [faxNumber, setFaxNumber] = useState('');
  const [faxData, setFaxData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const classes = useStyles();

  const fetchFaxDetails = async () => {
    if (!jobId) {
      setError('Missing job ID');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await getFaxById(jobId);

      if (response?.data.message) {
        setFaxData(response.data.message);
        if (response.data.message.fromNumber) {
          setFaxNumber(response.data.message.fromNumber);
        } else {
          setFaxNumber('Unknown');
        }
      } else {
        setFaxNumber('Unknown');
        setError('Failed to load fax details.');
      }
    } catch (err) {
      console.error('Error fetching fax details:', err);
      setError('Failed to load fax details. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchFaxDetails();
  }, [jobId]);

  const handleMarkAsReviewed = async () => {
    if (!jobId) {
      return;
    }

    try {
      setIsSubmitting(true);
      await toggleFaxStatus(jobId);
      await fetchFaxDetails();
    } catch (err) {
      console.error('Error toggling fax status:', err);
      setError('Failed to update fax status. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonText = faxData?.status === 'reviewed' ? 'Mark as not reviewed' : 'Mark as reviewed';
  const buttonIcon = faxData?.status === 'reviewed' ? 'close' : 'checkmark';

  const pdfUrl = `${faxData?.url}`;

  return (
    <BaseLayout className={classes.baseLayout}>
      <div className={classes.header}>
        <Breadcrumbs
          links={[
            { href: '/admin/faxes', text: 'Faxes' },
            {
              text: faxNumber ? `Fax from ${faxNumber}` : `Fax ${jobId}`,
            },
          ]}
        />
      </div>
      <Box sx={sxStyles.container}>
        <Box sx={sxStyles.header}>
          <Heading sx={sxStyles.headerTitle} color={colors.neutral900} weight={fontWeight.BOLD}>
            {`Fax from ${faxNumber}`}
          </Heading>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Icons color={colors.white} glyph={buttonIcon} size={20} />}
            onClick={handleMarkAsReviewed}
            disabled={isSubmitting}
            sx={sxStyles.markReviewedButton}
          >
            {isSubmitting ? 'Processing...' : buttonText}
          </Button>
        </Box>

        <Box sx={sxStyles.pdfContainer}>
          {isLoading ? (
            <Box sx={sxStyles.loadingContainer}>
              <Text level={textLevel.M}>Loading PDF...</Text>
            </Box>
          ) : error ? (
            <Box sx={sxStyles.errorContainer}>
              <Text level={textLevel.M} color={colors.error}>
                {error}
              </Text>
            </Box>
          ) : (
            <object data={pdfUrl} type="application/pdf" width="100%" height="100%"></object>
          )}
        </Box>
      </Box>
    </BaseLayout>
  );
};

export default FaxDetail;
